import { useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No results.",
  disabled = false,
  loading = false,
  error = false,
  testId,
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full flex items-center justify-between gap-2 bg-neutral-900 border rounded-sm px-4 py-3 text-left text-sm transition-colors outline-none",
            "focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 hover:border-neutral-600",
            error ? "border-red-500 ring-1 ring-red-500" : "border-neutral-800",
            disabled && "opacity-50 cursor-not-allowed",
            value ? "text-white" : "text-neutral-600"
          )}
        >
          <span className="truncate">{value || placeholder}</span>
          {loading ? (
            <Loader2 className="w-4 h-4 shrink-0 animate-spin text-neutral-500" />
          ) : (
            <ChevronsUpDown className="w-4 h-4 shrink-0 text-neutral-500" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-[#141414] border-neutral-800 w-[--radix-popover-trigger-width]"
        align="start"
      >
        <Command className="bg-[#141414]">
          <CommandInput placeholder={searchPlaceholder} className="text-white" />
          <CommandList>
            <CommandEmpty className="text-neutral-500">{emptyText}</CommandEmpty>
            {options.map((opt) => (
              <CommandItem
                key={opt}
                value={opt}
                data-testid={testId ? `${testId}-option` : undefined}
                onSelect={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className="text-neutral-200 aria-selected:bg-yellow-400/10 aria-selected:text-yellow-400 cursor-pointer"
              >
                <Check className={cn("mr-2 h-4 w-4 text-yellow-400", value === opt ? "opacity-100" : "opacity-0")} />
                {opt}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
